import React, { useState, useEffect, useMemo, useCallback } from "react";
import { safeJSONParse } from "../../../utils/helper";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
  IconButton,
  TextField,
  Button,
  Chip,
  Box,
  Tooltip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import {
  setTemplateReviewerChecklistFeedback,
  resetTemplateReviewerChecklistFeedback,
} from "../../../store/filtersSlice";

type TemplateReviewerChecklistAgentProps = {
  parsedContent: any;
  messageId: string;
};

type ChecklistItem = {
  key: string;
  label: string;
  value: string;
  isCustom?: boolean;
  category?: string;
  subcategory?: string;
  priority?: string;
  enabled?: boolean;
};

type CategoryGroup = {
  [category: string]: {
    [subcategory: string]: ChecklistItem[];
  };
};

// Default feedback object to prevent new object creation on every render
const DEFAULT_FEEDBACK = {
  accept_array: [],
  reject_array: [],
  modify_array: [],
  add_new_array: [],
};

// Helper function to get priority chip color
const getPriorityColor = (priority: string) => {
  switch (priority) {
    case "must_have":
      return { bg: "#fee", border: "#dc3545", text: "#dc3545" };
    case "negotiable":
      return { bg: "#fff3cd", border: "#ffc107", text: "#856404" };
    case "informational":
      return { bg: "#e3f2fd", border: "#2196f3", text: "#0d47a1" };
    default:
      return { bg: "#f0f0f0", border: "#999", text: "#666" };
  }
};

// Helper function to format priority label
const formatPriority = (priority: string): string => {
  return priority
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Copyable Checklist Item Component with Action Buttons
const ChecklistItemCard = ({
  item,
  onReject,
  onModify,
  onDelete,
  actionStatus,
  modifyText,
  onModifyTextChange,
  onModifySave,
  onModifyCancel,
}: {
  item: ChecklistItem;
  onReject: () => void;
  onModify: () => void;
  onDelete?: () => void;
  actionStatus?: string;
  modifyText?: string;
  onModifyTextChange?: (text: string) => void;
  onModifySave?: () => void;
  onModifyCancel?: () => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const priorityColors = item.priority ? getPriorityColor(item.priority) : null;

  return (
    <div
      style={{
        background: "#ffffff",
        padding: "16px",
        borderRadius: "8px",
        border: "1px solid #e0e0e0",
        marginBottom: "12px",
        position: "relative",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Header with Label and Action Status */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "12px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
          <span
            style={{
              fontSize: "14px",
              fontWeight: "500",
              color: "#004141",
            }}
          >
            {item.label}
          </span>
          {priorityColors && (
            <Chip
              label={formatPriority(item.priority!)}
              size="small"
              sx={{
                backgroundColor: priorityColors.bg,
                color: priorityColors.text,
                border: `1px solid ${priorityColors.border}`,
                fontSize: "10px",
                fontWeight: 600,
                height: "20px",
              }}
            />
          )}
          {item.isCustom && (
            <Chip
              label="Custom"
              size="small"
              sx={{
                backgroundColor: "#e3f2fd",
                color: "#00a888",
                fontSize: "11px",
                fontWeight: 600,
              }}
            />
          )}
          {actionStatus && (
            <Chip
              label={actionStatus}
              size="small"
              color={actionStatus === "Rejected" ? "error" : "primary"}
              sx={{ fontSize: "11px" }}
            />
          )}
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            opacity: isHovered ? 1 : 0,
            transition: "opacity 0.2s ease-in-out",
            pointerEvents: isHovered ? "auto" : "none",
          }}
        >
          {actionStatus !== "Modifying" && (
            <>
              <Tooltip
                title={actionStatus === "Rejected" ? "Undo Reject" : "Reject"}
                arrow
              >
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onReject();
                  }}
                  size="small"
                  sx={{
                    padding: "4px",
                    color: "#bd1919",
                    minWidth: "24px",
                    "&:hover": {
                      backgroundColor: "#fee",
                    },
                  }}
                >
                  <CancelIcon sx={{ fontSize: "16px" }} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Modify" arrow>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onModify();
                  }}
                  size="small"
                  sx={{
                    padding: "4px",
                    color: "#00a888",
                    minWidth: "24px",
                    "&:hover": {
                      backgroundColor: "#e3f2fd",
                    },
                  }}
                >
                  <EditIcon sx={{ fontSize: "16px" }} />
                </IconButton>
              </Tooltip>

              {onDelete && (
                <Tooltip title="Delete" arrow>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete();
                    }}
                    size="small"
                    sx={{
                      padding: "4px",
                      color: "#bd1919",
                      minWidth: "24px",
                      "&:hover": {
                        backgroundColor: "#fee",
                      },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: "16px" }} />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Original Content */}
      {actionStatus !== "Modifying" && (
        <div
          style={{
            background: "#f9fafb",
            padding: "12px",
            borderRadius: "6px",
            border: "1px solid #e0e0e0",
          }}
        >
          <p
            style={{
              fontSize: "14px",
              color: "#403f3f",
              margin: 0,
              lineHeight: "1.6",
              whiteSpace: "pre-wrap",
            }}
          >
            {item.value}
          </p>
        </div>
      )}

      {/* Modified Content */}
      {actionStatus === "Modified" &&
        modifyText &&
        modifyText !== item.value && (
          <div style={{ marginTop: "12px" }}>
            <div
              style={{
                background: "#e3f2fd",
                padding: "12px",
                borderRadius: "6px",
                border: "1px solid #00a888",
              }}
            >
              <p
                style={{
                  fontSize: "13px",
                  color: "#00a888",
                  fontWeight: "600",
                  marginBottom: "8px",
                }}
              >
                Modified Value:
              </p>
              <p
                style={{
                  fontSize: "14px",
                  color: "#403f3f",
                  margin: 0,
                  lineHeight: "1.6",
                  whiteSpace: "pre-wrap",
                }}
              >
                {modifyText}
              </p>
            </div>
          </div>
        )}

      {/* Modify Mode */}
      {actionStatus === "Modifying" && (
        <div style={{ marginTop: "8px" }}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Modify Value"
            variant="outlined"
            value={modifyText}
            onChange={(e) => onModifyTextChange?.(e.target.value)}
            size="small"
            sx={{ marginBottom: "8px" }}
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={onModifySave}
              sx={{
                backgroundColor: "#5fcfac",
                "&:hover": { backgroundColor: "#4fb89a" },
                textTransform: "none",
              }}
            >
              Save
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={onModifyCancel}
              sx={{
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
          </Box>
        </div>
      )}
    </div>
  );
};

export default function TemplateReviewerChecklistAgent({
  parsedContent,
  messageId,
}: TemplateReviewerChecklistAgentProps): JSX.Element {
  // Memoize finalAnswer to prevent infinite re-renders
  const finalAnswer = useMemo(
    () => safeJSONParse(parsedContent?.finalAnswer),
    [parsedContent?.finalAnswer]
  );
  const question = parsedContent?.question;
  const dispatch = useAppDispatch();

  // Get template reviewer checklist feedback from Redux for this specific message
  const messageFeedback = useSelector(
    (state: RootState) =>
      state.filters.templateReviewerChecklistFeedback[messageId] ||
      DEFAULT_FEEDBACK
  );

  const { reject_array, modify_array, add_new_array } = messageFeedback;

  // State for checklist items organized by category
  const [categoryGroups, setCategoryGroups] = useState<CategoryGroup>({});

  // State for collapsed categories and subcategories
  const [collapsedCategories, setCollapsedCategories] = useState<{
    [key: string]: boolean;
  }>({});
  const [collapsedSubcategories, setCollapsedSubcategories] = useState<{
    [key: string]: boolean;
  }>({});

  // State for tracking action status of each item
  const [itemActions, setItemActions] = useState<{ [key: string]: string }>({});

  // State for modify text inputs
  const [modifyTexts, setModifyTexts] = useState<{ [key: string]: string }>({});

  // State for Add New modal
  const [addNewModalOpen, setAddNewModalOpen] = useState(false);
  const [addNewType, setAddNewType] = useState<"subsection" | "item">("item");
  const [addNewCategory, setAddNewCategory] = useState("");
  const [addNewSubcategory, setAddNewSubcategory] = useState("");
  const [addNewName, setAddNewName] = useState("");
  const [addNewItemName, setAddNewItemName] = useState(""); // For subsection's first item name
  const [addNewDescription, setAddNewDescription] = useState("");
  const [addNewPriority, setAddNewPriority] = useState<
    "must_have" | "negotiable" | "informational"
  >("must_have");

  // Helper function to format label from key
  const formatLabel = useCallback((key: string): string => {
    return key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }, []);

  // Initialize checklist items from payload (organized by category and subcategory)
  useEffect(() => {
    if (finalAnswer?.payload?.by_category) {
      const groups: CategoryGroup = {};

      // Parse by_category structure
      Object.entries(finalAnswer.payload.by_category).forEach(
        ([category, items]: [string, any]) => {
          if (!groups[category]) {
            groups[category] = {};
          }

          // Group items by subcategory
          items.forEach((item: any) => {
            const subcategory = item.subcategory || "Other";

            if (!groups[category][subcategory]) {
              groups[category][subcategory] = [];
            }

            groups[category][subcategory].push({
              key: item.id,
              label: item.name,
              value: item.description,
              category: category,
              subcategory: subcategory,
              priority: item.priority,
              enabled: item.enabled,
              isCustom: false,
            });
          });
        }
      );

      // Only update if structure is actually different
      setCategoryGroups((prev) => {
        const prevKeys = JSON.stringify(Object.keys(prev).sort());
        const newKeys = JSON.stringify(Object.keys(groups).sort());
        return prevKeys === newKeys ? prev : groups;
      });

      // Initialize collapsed states only for NEW categories/subcategories
      // This preserves user's expand/collapse choices
      setCollapsedCategories((prev) => {
        const updated = { ...prev };
        Object.keys(groups).forEach((category) => {
          if (!(category in prev)) {
            updated[category] = true; // New category starts collapsed
          }
        });
        return updated;
      });

      setCollapsedSubcategories((prev) => {
        const updated = { ...prev };
        Object.entries(groups).forEach(([category, subcategories]) => {
          Object.keys(subcategories).forEach((subcategory) => {
            const subcategoryKey = `${category}-${subcategory}`;
            if (!(subcategoryKey in prev)) {
              updated[subcategoryKey] = true; // New subcategory starts collapsed
            }
          });
        });
        return updated;
      });
    }
  }, [finalAnswer]);

  // Load newly added items from add_new_array into categoryGroups
  useEffect(() => {
    if (add_new_array.length > 0) {
      setCategoryGroups((prev) => {
        const updated = { ...prev };

        add_new_array.forEach((newItem: any) => {
          const category = newItem.category;
          const subcategory = newItem.subcategory;

          // Initialize category if it doesn't exist
          if (!updated[category]) {
            updated[category] = {};
          }

          // Initialize subcategory if it doesn't exist
          if (!updated[category][subcategory]) {
            updated[category][subcategory] = [];
          }

          // Check if item already exists (avoid duplicates)
          const existingItem = updated[category][subcategory].find(
            (item) => item.key === newItem.key
          );
          if (!existingItem) {
            updated[category][subcategory].push({
              key: newItem.key,
              label: newItem.label,
              value: newItem.value,
              category: category,
              subcategory: subcategory,
              priority: newItem.priority,
              enabled: true,
              isCustom: true,
            });
          }
        });

        return updated;
      });
    }
  }, [add_new_array]);

  // Sync action status and modify texts from Redux whenever arrays change
  useEffect(() => {
    const newItemActions: { [key: string]: string } = {};
    const newModifyTexts: { [key: string]: string } = {};

    // Check reject array
    reject_array.forEach((item) => {
      if (item._key) {
        newItemActions[item._key] = "Rejected";
      }
    });

    // Check modify array and store modified values
    modify_array.forEach((item) => {
      if (item._key) {
        newItemActions[item._key] = "Modified";
        newModifyTexts[item._key] = item.value;
      }
    });

    // Only update state if values have actually changed
    setItemActions((prev) => {
      const prevKeys = Object.keys(prev).sort().join(",");
      const newKeys = Object.keys(newItemActions).sort().join(",");
      const prevValues = Object.values(prev).sort().join(",");
      const newValues = Object.values(newItemActions).sort().join(",");
      return prevKeys === newKeys && prevValues === newValues
        ? prev
        : newItemActions;
    });

    setModifyTexts((prev) => {
      const prevKeys = Object.keys(prev).sort().join(",");
      const newKeys = Object.keys(newModifyTexts).sort().join(",");
      const prevValues = Object.values(prev).sort().join(",");
      const newValues = Object.values(newModifyTexts).sort().join(",");
      return prevKeys === newKeys && prevValues === newValues
        ? prev
        : newModifyTexts;
    });
  }, [reject_array, modify_array]);

  // Clear Redux state when showing final results (accept_array/modify_array from API)
  // This ensures the next checklist doesn't carry over previous selections
  useEffect(() => {
    if (
      (finalAnswer?.accept_array && finalAnswer.accept_array.length > 0) ||
      (finalAnswer?.modify_array && finalAnswer.modify_array.length > 0)
    ) {
      // Clear the Redux state for this messageId so it doesn't carry over to next interaction
      dispatch(resetTemplateReviewerChecklistFeedback(messageId));
    }
  }, [finalAnswer, messageId, dispatch]);

  if (!finalAnswer) {
    return <div>No data available</div>;
  }

  // Helper function to create item key
  const getItemKey = (itemKey: string) => `checklist-${itemKey}`;

  // Reject handler (toggles rejection)
  const handleReject = (item: ChecklistItem) => {
    const key = getItemKey(item.key);

    // Check if already rejected
    const isAlreadyRejected = reject_array.some((i) => i._key === key);

    if (isAlreadyRejected) {
      // Undo rejection - remove from reject array
      const newRejectArray = reject_array.filter((i) => i._key !== key);

      dispatch(
        setTemplateReviewerChecklistFeedback({
          messageId,
          feedback: {
            accept_array: [],
            reject_array: newRejectArray,
            modify_array: modify_array,
            add_new_array: add_new_array,
          },
        })
      );

      // Clear action status
      setItemActions((prev) => {
        const newActions = { ...prev };
        delete newActions[key];
        return newActions;
      });
    } else {
      // Reject item - remove from modify array and add to reject array
      const newModifyArray = modify_array.filter((i) => i._key !== key);
      const newRejectArray = [...reject_array, { ...item, _key: key }];

      dispatch(
        setTemplateReviewerChecklistFeedback({
          messageId,
          feedback: {
            accept_array: [],
            reject_array: newRejectArray,
            modify_array: newModifyArray,
            add_new_array: add_new_array,
          },
        })
      );

      setItemActions((prev) => ({ ...prev, [key]: "Rejected" }));
    }
  };

  // Modify handler (initiates modify mode)
  const handleModify = (item: ChecklistItem) => {
    const key = getItemKey(item.key);

    // Set the action status to "Modifying" to show text input
    setItemActions((prev) => ({ ...prev, [key]: "Modifying" }));

    // Check if there's already a modified version in Redux
    const existingModifiedItem = modify_array.find((i) => i._key === key);

    // Initialize modify text with previously modified text if exists, otherwise use original
    const textToLoad = existingModifiedItem
      ? existingModifiedItem.value
      : item.value;

    setModifyTexts((prev) => ({ ...prev, [key]: textToLoad }));
  };

  // Cancel modify handler
  const handleModifyCancel = (item: ChecklistItem) => {
    const key = getItemKey(item.key);

    // Check if item was previously modified
    const wasModified = modify_array.some((i) => i._key === key);

    if (wasModified) {
      setItemActions((prev) => ({ ...prev, [key]: "Modified" }));
    } else {
      setItemActions((prev) => {
        const newActions = { ...prev };
        delete newActions[key];
        return newActions;
      });
    }

    setModifyTexts((prev) => {
      const newTexts = { ...prev };
      delete newTexts[key];
      return newTexts;
    });
  };

  // Save modification handler
  const handleModifySave = (item: ChecklistItem) => {
    const key = getItemKey(item.key);
    const modifiedText = modifyTexts[key];

    if (!modifiedText || modifiedText.trim() === "") {
      return;
    }

    // Remove from reject array if exists
    const newRejectArray = reject_array.filter((i) => i._key !== key);

    // Create modified item
    const modifiedItem = {
      ...item,
      value: modifiedText,
      _key: key,
    };

    // Add to modify array
    const exists = modify_array.some((i) => i._key === key);
    const newModifyArray = exists
      ? modify_array.map((i) => (i._key === key ? modifiedItem : i))
      : [...modify_array, modifiedItem];

    // Update Redux with messageId
    dispatch(
      setTemplateReviewerChecklistFeedback({
        messageId,
        feedback: {
          accept_array: [],
          reject_array: newRejectArray,
          modify_array: newModifyArray,
          add_new_array: add_new_array,
        },
      })
    );

    setItemActions((prev) => ({ ...prev, [key]: "Modified" }));
  };

  // Delete handler (only for custom items)
  const handleDelete = (item: ChecklistItem) => {
    const key = getItemKey(item.key);

    // Remove from all arrays
    const newRejectArray = reject_array.filter((i) => i._key !== key);
    const newModifyArray = modify_array.filter((i) => i._key !== key);
    const newAddNewArray = add_new_array.filter((i) => i._key !== key);

    // Update Redux with messageId
    dispatch(
      setTemplateReviewerChecklistFeedback({
        messageId,
        feedback: {
          accept_array: [],
          reject_array: newRejectArray,
          modify_array: newModifyArray,
          add_new_array: newAddNewArray,
        },
      })
    );

    // Remove from local state (category groups)
    setCategoryGroups((prev) => {
      const newGroups = { ...prev };
      if (item.category && item.subcategory) {
        const subcategoryItems = newGroups[item.category]?.[item.subcategory];
        if (subcategoryItems) {
          newGroups[item.category][item.subcategory] = subcategoryItems.filter(
            (i) => i.key !== item.key
          );
          // Remove empty subcategories
          if (newGroups[item.category][item.subcategory].length === 0) {
            delete newGroups[item.category][item.subcategory];
          }
          // Remove empty categories
          if (Object.keys(newGroups[item.category]).length === 0) {
            delete newGroups[item.category];
          }
        }
      }
      return newGroups;
    });

    // Clear action status
    setItemActions((prev) => {
      const newActions = { ...prev };
      delete newActions[key];
      return newActions;
    });
  };

  // Handler to open Add New modal for subsection
  const handleAddNewSubsection = (category: string) => {
    setAddNewType("subsection");
    setAddNewCategory(category);
    setAddNewSubcategory("");
    setAddNewName("");
    setAddNewItemName("");
    setAddNewDescription("");
    setAddNewPriority("must_have");
    setAddNewModalOpen(true);
  };

  // Handler to open Add New modal for item
  const handleAddNewItem = (category: string, subcategory: string) => {
    setAddNewType("item");
    setAddNewCategory(category);
    setAddNewSubcategory(subcategory);
    setAddNewName("");
    setAddNewDescription("");
    setAddNewPriority("must_have");
    setAddNewModalOpen(true);
  };

  // Handler to close Add New modal
  const handleCloseAddNewModal = () => {
    setAddNewModalOpen(false);
    setAddNewName("");
    setAddNewItemName("");
    setAddNewDescription("");
    setAddNewPriority("must_have");
  };

  // Handler to submit Add New
  const handleSubmitAddNew = () => {
    // Validation based on type
    if (addNewType === "subsection") {
      if (
        !addNewName.trim() ||
        !addNewItemName.trim() ||
        !addNewDescription.trim()
      ) {
        return;
      }
    } else {
      if (!addNewName.trim() || !addNewDescription.trim()) {
        return;
      }
    }

    const timestamp = Date.now();
    const newItemKey = `custom-${timestamp}`;

    let finalCategory = addNewCategory;
    let finalSubcategory = addNewSubcategory;

    // If adding a subsection, the subsection name is the new name
    if (addNewType === "subsection") {
      finalSubcategory = addNewName;
      // For subsection, use the item name from the input
      const newItem = {
        key: newItemKey,
        label: addNewItemName, // Use the item name provided by user
        value: addNewDescription,
        category: finalCategory,
        subcategory: finalSubcategory,
        priority: addNewPriority,
        enabled: true,
        isCustom: true,
        _key: getItemKey(newItemKey),
      };

      const newAddNewArray = [...add_new_array, newItem];

      dispatch(
        setTemplateReviewerChecklistFeedback({
          messageId,
          feedback: {
            accept_array: [],
            reject_array: reject_array,
            modify_array: modify_array,
            add_new_array: newAddNewArray,
          },
        })
      );
    } else {
      // Adding an item to existing subcategory
      const newItem = {
        key: newItemKey,
        label: addNewName,
        value: addNewDescription,
        category: finalCategory,
        subcategory: finalSubcategory,
        priority: addNewPriority,
        enabled: true,
        isCustom: true,
        _key: getItemKey(newItemKey),
      };

      const newAddNewArray = [...add_new_array, newItem];

      dispatch(
        setTemplateReviewerChecklistFeedback({
          messageId,
          feedback: {
            accept_array: [],
            reject_array: reject_array,
            modify_array: modify_array,
            add_new_array: newAddNewArray,
          },
        })
      );
    }

    handleCloseAddNewModal();
  };

  // Calculate total items count
  const totalItems = Object.values(categoryGroups).reduce((total, category) => {
    return (
      total +
      Object.values(category).reduce(
        (subTotal, items) => subTotal + items.length,
        0
      )
    );
  }, 0);

  // Toggle category collapse
  const toggleCategory = (category: string) => {
    setCollapsedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  // Toggle subcategory collapse
  const toggleSubcategory = (category: string, subcategory: string) => {
    const key = `${category}-${subcategory}`;
    setCollapsedSubcategories((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <div>
      {/* Header */}
      <Box
        sx={{
          marginBottom: "20px",
          paddingBottom: "12px",
          borderBottom: "2px solid #e0e0e0",
        }}
      >
        <h3
          style={{
            fontSize: "16px",
            fontWeight: "600",
            margin: 0,
          }}
        >
          Below is the checklist I am going to follow for review
        </h3>
        <h3
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: "#004141",
            margin: 0,
          }}
        >
          Template Review Checklist ({totalItems} items)
        </h3>
      </Box>

      {/* Checklist Items by Category */}
      <div style={{ marginTop: "16px" }}>
        {Object.entries(categoryGroups).map(([category, subcategories]) => {
          const isCategoryCollapsed = collapsedCategories[category];
          const categoryItemCount = Object.values(subcategories).reduce(
            (total, items) => total + items.length,
            0
          );

          return (
            <div key={category} style={{ marginBottom: "24px" }}>
              {/* Category Header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  padding: "12px 16px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "8px",
                  border: "1px solid #e0e0e0",
                  marginBottom: "12px",
                }}
              >
                <Box
                  onClick={() => toggleCategory(category)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flex: 1,
                    cursor: "pointer",
                    "&:hover": {
                      opacity: 0.8,
                    },
                  }}
                >
                  {isCategoryCollapsed ? (
                    <ExpandMoreIcon />
                  ) : (
                    <ExpandLessIcon />
                  )}
                  <span
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#004141",
                    }}
                  >
                    {category}
                  </span>
                </Box>
                <Chip
                  label={`${categoryItemCount} items`}
                  size="small"
                  sx={{
                    backgroundColor: "#00a888",
                    color: "#ffffff",
                    fontSize: "11px",
                    fontWeight: 600,
                  }}
                />
                <Tooltip title="Add New Subsection" arrow>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddNewSubsection(category);
                    }}
                    sx={{
                      padding: "4px",
                      color: "#00a888",
                      "&:hover": {
                        backgroundColor: "rgba(0, 168, 136, 0.1)",
                      },
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Category Content */}
              <Collapse in={!isCategoryCollapsed}>
                <div style={{ paddingLeft: "16px" }}>
                  {Object.entries(subcategories).map(([subcategory, items]) => {
                    const subcategoryKey = `${category}-${subcategory}`;
                    const isSubcategoryCollapsed =
                      collapsedSubcategories[subcategoryKey];

                    return (
                      <div
                        key={subcategoryKey}
                        style={{ marginBottom: "16px" }}
                      >
                        {/* Subcategory Header */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            padding: "8px 12px",
                            backgroundColor: "#ffffff",
                            borderRadius: "6px",
                            border: "1px solid #e0e0e0",
                            marginBottom: "8px",
                          }}
                        >
                          <Box
                            onClick={() =>
                              toggleSubcategory(category, subcategory)
                            }
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flex: 1,
                              cursor: "pointer",
                              "&:hover": {
                                opacity: 0.8,
                              },
                            }}
                          >
                            {isSubcategoryCollapsed ? (
                              <ExpandMoreIcon fontSize="small" />
                            ) : (
                              <ExpandLessIcon fontSize="small" />
                            )}
                            <span
                              style={{
                                fontSize: "14px",
                                fontWeight: "500",
                                color: "#004141",
                              }}
                            >
                              {subcategory}
                            </span>
                          </Box>
                          <Chip
                            label={items.length}
                            size="small"
                            sx={{
                              backgroundColor: "#e3f2fd",
                              color: "#00a888",
                              fontSize: "10px",
                              fontWeight: 600,
                              height: "20px",
                              minWidth: "24px",
                            }}
                          />
                          <Tooltip title="Add New Item" arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddNewItem(category, subcategory);
                              }}
                              sx={{
                                padding: "4px",
                                color: "#00a888",
                                "&:hover": {
                                  backgroundColor: "rgba(0, 168, 136, 0.1)",
                                },
                              }}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>

                        {/* Subcategory Items */}
                        <Collapse in={!isSubcategoryCollapsed}>
                          <div style={{ paddingLeft: "8px" }}>
                            {items.map((item) => {
                              const key = getItemKey(item.key);
                              return (
                                <ChecklistItemCard
                                  key={item.key}
                                  item={item}
                                  onReject={() => handleReject(item)}
                                  onModify={() => handleModify(item)}
                                  onDelete={
                                    item.isCustom
                                      ? () => handleDelete(item)
                                      : undefined
                                  }
                                  actionStatus={itemActions[key]}
                                  modifyText={modifyTexts[key] || ""}
                                  onModifyTextChange={(text: string) =>
                                    setModifyTexts((prev) => ({
                                      ...prev,
                                      [key]: text,
                                    }))
                                  }
                                  onModifySave={() => handleModifySave(item)}
                                  onModifyCancel={() =>
                                    handleModifyCancel(item)
                                  }
                                />
                              );
                            })}
                          </div>
                        </Collapse>
                      </div>
                    );
                  })}
                </div>
              </Collapse>
            </div>
          );
        })}
      </div>

      {/* Question Section*/}
      {question && (
        <div
          style={{
            marginTop: "20px",
            paddingTop: "16px",
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <p
            style={{
              fontSize: "16px",
              color: "#004141",
              fontWeight: "600",
              margin: 0,
            }}
          >
            {question}
          </p>
        </div>
      )}

      {/* Add New Modal */}
      <Dialog
        open={addNewModalOpen}
        onClose={handleCloseAddNewModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: "#004141", fontWeight: 600 }}>
          {addNewType === "subsection" ? "Add New Subsection" : "Add New Item"}
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              marginTop: 2,
            }}
          >
            {addNewType === "subsection" && (
              <TextField
                label="Category"
                variant="outlined"
                fullWidth
                value={addNewCategory}
                disabled
              />
            )}
            {addNewType === "item" && (
              <TextField
                label="Subcategory"
                variant="outlined"
                fullWidth
                value={addNewSubcategory}
                disabled
              />
            )}
            <TextField
              label={
                addNewType === "subsection" ? "Subsection Name" : "Item Name"
              }
              variant="outlined"
              fullWidth
              value={addNewName}
              onChange={(e) => setAddNewName(e.target.value)}
              placeholder={
                addNewType === "subsection"
                  ? "e.g., Payment Terms"
                  : "e.g., Late Payment Penalties"
              }
            />
            {addNewType === "subsection" && (
              <TextField
                label="Item Name"
                variant="outlined"
                fullWidth
                value={addNewItemName}
                onChange={(e) => setAddNewItemName(e.target.value)}
                placeholder="e.g., Payment Schedule"
              />
            )}
            <TextField
              label="Description"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              value={addNewDescription}
              onChange={(e) => setAddNewDescription(e.target.value)}
              placeholder="Enter the detailed description..."
            />
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={addNewPriority}
                label="Priority"
                onChange={(e) =>
                  setAddNewPriority(
                    e.target.value as
                      | "must_have"
                      | "negotiable"
                      | "informational"
                  )
                }
              >
                <MenuItem value="must_have">Must Have</MenuItem>
                <MenuItem value="negotiable">Negotiable</MenuItem>
                <MenuItem value="informational">Informational</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button
            onClick={handleCloseAddNewModal}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitAddNew}
            variant="contained"
            disabled={
              addNewType === "subsection"
                ? !addNewName.trim() ||
                  !addNewItemName.trim() ||
                  !addNewDescription.trim()
                : !addNewName.trim() || !addNewDescription.trim()
            }
            sx={{
              backgroundColor: "#00a888",
              "&:hover": { backgroundColor: "#00947a" },
              textTransform: "none",
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
